import { useState, useEffect } from 'react';
import { MapPin, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CountrySelectorProps {
  selectedCountry: string | null;
  onCountryChange: (country: string | null) => void;
  detectedCountry: string;
}

export default function CountrySelector({
  selectedCountry,
  onCountryChange,
  detectedCountry,
}: CountrySelectorProps) {
  const { t } = useTranslation('crisis');
  const [countries, setCountries] = useState<string[]>([]);

  useEffect(() => {
    async function fetchCountries() {
      const { data } = await supabase
        .from('emergency_contacts')
        .select('country_name')
        .order('country_name');

      if (data) {
        setCountries(data.map(c => c.country_name));
      }
    }

    fetchCountries();
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-earth-200 p-4 mb-6">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-center gap-2 text-earth-700">
          <MapPin className="h-5 w-5" />
          <span className="font-medium">
            {t('yourLocation')}: <span className="text-primary">{selectedCountry || detectedCountry}</span>
          </span>
        </div>

        <div className="flex items-center gap-2 flex-1 md:justify-end">
          <Select
            value={selectedCountry || ''}
            onValueChange={(value) => onCountryChange(value || null)}
          >
            <SelectTrigger className="w-full md:w-[300px]">
              <SelectValue placeholder={t('selectCountry', 'Select a country...')} />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem key={country} value={country}>
                  {country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedCountry && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCountryChange(null)}
            >
              {t('reset', 'Reset')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
